import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Info, X, Check } from "lucide-react";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { MentionProvider } from "../SearchArea/MentionContext.jsx";
import useEventStore from "../../Store/EventStore.jsx";
import { useCurrencyFormatter } from "../../Hooks/useCurrencyFormatter.js";
import { useProductAnalytics } from "../../Hooks/useProductAnalytics.js";
import { useProductActions } from "../../Hooks/useProductActions.js";
import { useTranslation } from "react-i18next";
import useCartStore from "../../Store/CartStore.jsx";
import { useHistory } from "react-router-dom";

// Molecules
import ProductInfo from "./molecules/ProductInfo.jsx";
import ProductTitle from "../ui/ProductTitle.jsx";
import ProductTags from "./molecules/ProductTags.jsx";
import ProductActions from "./molecules/ProductActions.jsx";
import ProductFavoriteButton from "./molecules/ProductFavoriteButton.jsx";
import ProductLink from "./molecules/ProductsLink.jsx";

// Atomic Components
import CardContainer from "../ui/Cards/CardContainer.jsx";
import CardHeader from "../ui/Cards/CardHeader.jsx";
import BadgeOverlay from "../ui/Cards/BadgeOverlay.jsx";
import CardContent from "../ui/Cards/CardContent.jsx";

/**
 * ProductCard Organism - Unified product card component
 * Supports two variants: 'search' (for search results) and 'event' (for events/spaces)
 */
const ProductCard = ({
    // Common props
    id,
    opId,
    image,
    title,
    price,
    url,
    lovemeter,
    categories = [],
    categoriesTranslated = [],
    description,
    explanation,

    // Variant configuration
    variant = "search", // 'search' | 'event'

    // Event-specific props
    heart,
    type,
    name,
    prompt,
    country,

    // Optional callbacks
    onTagClick,
    handleSearchTextChange,
    onAddToCart: customAddToCart,

    // Container props
    containerClassName = "",
    containerRef = null,
    addToCartText,
    buyNowText,
    showCategories = true,
    showLovemeter = true,
    showExplanation = true,
    showFavoriteButton = true,
    isSelected = false,
}) => {
    const { t, i18n } = useTranslation();
    const { addToCart } = useCartStore();
    const currentLang = i18n.language.split("-")[0].toLowerCase();

    const handleAddToCart = (e) => {
        e.stopPropagation();
        if (customAddToCart) {
            customAddToCart(e);
        } else {
            addToCart({ id, image, title, price, url });
        }
    };

    const displayCategories = (currentLang === "de" && categoriesTranslated?.length > 0)
        ? categoriesTranslated
        : categories;
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [openComponentID, setOpenComponentID] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [tooltipState, setTooltipState] = useState({ visible: false, isPinned: false });
    const tooltipRef = useRef(null);
    const timeoutRef = useRef(null);

    const closeTooltip = () => {
        setTooltipState({ visible: false, isPinned: false });
    };

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setTooltipState(prev => ({ ...prev, visible: true }));
    };

    const handleMouseLeave = () => {
        if (tooltipState.isPinned) return;
        timeoutRef.current = setTimeout(() => {
            setTooltipState(prev => ({ ...prev, visible: prev.isPinned }));
        }, 200); // 200ms buffer for moving between label and tooltip
    };

    // Click away and Escape logic
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                closeTooltip();
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                closeTooltip();
            }
        };

        if (tooltipState.isPinned) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [tooltipState.isPinned]);

    // Hooks
    const { currency, formatPrice } = useCurrencyFormatter(country);
    const { logProductClick } = useProductAnalytics(id, opId);
    const { handleRedirect, handleTagClick: originalHandleTagClick, handleSearchRedirect } = useProductActions(
        id,
        url,
        logProductClick
    );

    const handleTagClick = (tagId, tag, index) => {
        const tagToSend = (categories && categories[index]) ? categories[index] : tag;
        originalHandleTagClick(tagId, tagToSend);
    };
    const { handleGiftDeletion, handleGiftAddition } = useEventStore();
    const history = useHistory();

    const handleBuyNow = (e) => {
        e.stopPropagation();
        addToCart({ id, image, title, price, url });
        history.push("/checkout");
    };

    // Event variant specific handlers
    const handleHeartClick = () => {
        if (variant === "event") {
            if (heart === "delete") {
                handleGiftDeletion(type, name, id);
            } else {
                setIsAnimating(true);
                handleGiftAddition(type, name, [id]);
            }
        }
    };

    // Determine which price to display and whether to format
    const displayPrice = variant === "search" ? formatPrice(price) : price;

    const isSpecial = !!explanation;

    // Extra container classes
    const extraContainerClasses = `
    container_shadowborder-solid border-1s border-neutral-green-100 
    mb-4 sm:m-2 md:m-6 
    ${variant === "event" && isAnimating ? "animate-move-up-disappear" : ""}
  `;

    if (imageError) return null;

    return (
        <MentionProvider>
            <div className={`relative ${containerClassName}`} ref={containerRef}>
                <CardContainer
                    variant={isSpecial ? "special" : "default"}
                    className={extraContainerClasses}
                >
                    {/* Product Header with Image and Favorite Button */}
                    <CardHeader
                        image={image}
                        alt="Product Image"
                        onImageClick={handleRedirect}
                        onImageLoad={() => setImageLoaded(true)}
                        onImageError={() => setImageError(true)}
                        imageLoaded={imageLoaded}
                        imageStyle={{ width: "250px", height: "250px" }}
                        imageClassName="w-[250px] h-[250px] max-w-[250px]"
                    >
                        {showFavoriteButton && (
                            <ProductFavoriteButton
                                variant={variant}
                                giftId={id}
                                giftImage={image}
                                heart={heart}
                                onHeartClick={handleHeartClick}
                                isOpen={openComponentID === id}
                                setOpenComponentID={setOpenComponentID}
                            />
                        )}
                        <ProductLink url={url} />
                        {isSelected && (
                            <div className="absolute top-0 right-0 z-[60] bg-red-600 text-white rounded-full p-1.5 shadow-lg pointer-events-none transform translate-x-1/2 -translate-y-1/2">
                                <Check size={18} strokeWidth={4} />
                            </div>
                        )}
                    </CardHeader>

                    {/* Product Content */}
                    <CardContent>
                        {/* Price and Lovemeter */}
                        <ProductInfo
                            price={displayPrice}
                            currency={currency}
                            lovemeter={lovemeter}
                            isSpecial={isSpecial}
                            showLovemeter={showLovemeter}
                        />

                        {/* Product Title */}
                        <ProductTitle title={title} onClick={handleRedirect} isSpecial={isSpecial} />

                        {showCategories && (
                            <ProductTags
                                categories={displayCategories}
                                onTagClick={handleTagClick}
                                productId={id}
                                variant={isSpecial ? "special" : (variant === "event" ? "solid" : "outline")}
                            />
                        )}

                        <ProductActions
                            onViewGift={handleRedirect}
                            onAddToCart={handleAddToCart}
                            onBuyNow={handleBuyNow}
                            className="w-full"
                            isSpecial={isSpecial}
                            addToCartText={addToCartText}
                            buyNowText={buyNowText}
                            isSelected={isSelected}
                        />
                        {showExplanation && (
                            <div className="flex justify-end items-center mt-3 mr-1 relative">
                                <motion.span
                                    className={`text-[10px] uppercase tracking-widest font-black transition-all duration-300 cursor-pointer flex items-center gap-2 ${explanation
                                        ? isSpecial ? "text-white/80 hover:text-white" : "text-red-500 hover:text-red-600"
                                        : "text-neutral-400 hover:text-neutral-600"}`}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTooltipState({ visible: true, isPinned: true });
                                    }}
                                    animate={explanation ? {
                                        x: [0, 2, 0],
                                        transition: { duration: 3, repeat: Infinity }
                                    } : {}}
                                >
                                    {explanation ? (
                                        <>
                                            <Sparkles className="w-3.5 h-3.5" />
                                            <span>Why this gift</span>
                                        </>
                                    ) : (
                                        <>
                                            <Info className="w-3.5 h-3.5" />
                                            <span>Description</span>
                                        </>
                                    )}
                                </motion.span>
                                <AnimatePresence>
                                    {tooltipState.visible && (
                                        <motion.div
                                            ref={tooltipRef}
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            className={`absolute bottom-full right-0 mb-3 w-64 p-4 border rounded-[24px] shadow-2xl z-50 overflow-hidden ${isSpecial
                                                ? " backdrop-blur-xl border-red-300"
                                                : "bg-white border-neutral-100"
                                                }`}
                                            onMouseEnter={handleMouseEnter}
                                            onMouseLeave={handleMouseLeave}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                {explanation ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[11px] font-black uppercase tracking-wider text-neutral-500">Why this gift?</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <div className="p-1 bg-neutral-100 rounded-lg">
                                                            <Info className="w-3.5 h-3.5 text-neutral-500" />
                                                        </div>
                                                        <span className="text-[11px] font-black uppercase tracking-wider text-neutral-500">{t("gift.productDetails")}</span>
                                                    </div>
                                                )}

                                                {tooltipState.isPinned && (
                                                    <button
                                                        onClick={closeTooltip}
                                                        className={`p-1 rounded-full transition-colors ${isSpecial ? "hover:bg-white/20" : "hover:bg-neutral-100"}`}
                                                    >
                                                        <X className={`w-3.5 h-3.5 ${isSpecial ? "text-white/100" : "text-neutral-400"}`} />
                                                    </button>
                                                )}
                                            </div>

                                            <div className={`text-[13px] font-medium leading-relaxed text-left normal-case max-h-[160px] overflow-y-auto scrollbar-thin ${isSpecial ? "text-white scrollbar-thumb-white/30" : "text-neutral-700 scrollbar-thumb-neutral-200"} scrollbar-track-transparent pr-1`}>
                                                {explanation ? (
                                                    <motion.div
                                                        initial="hidden"
                                                        animate="visible"
                                                        variants={{
                                                            visible: {
                                                                transition: {
                                                                    staggerChildren: 0.008
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        {explanation.split("").map((char, index) => (
                                                            <motion.span
                                                                key={index}
                                                                variants={{
                                                                    hidden: { opacity: 0, y: 5 },
                                                                    visible: { opacity: 1, y: 0 }
                                                                }}
                                                            >
                                                                {char}
                                                            </motion.span>
                                                        ))}
                                                    </motion.div>
                                                ) : (
                                                    <p>{description || t("gift.noDescription")}</p>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}
                    </CardContent>
                </CardContainer>
            </div>
        </MentionProvider >
    );
};

ProductCard.propTypes = {
    id: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    url: PropTypes.string.isRequired,
    lovemeter: PropTypes.number,
    categories: PropTypes.array,
    categoriesTranslated: PropTypes.array,
    description: PropTypes.string,
    explanation: PropTypes.string,
    variant: PropTypes.oneOf(["search", "event"]),
    heart: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string,
    prompt: PropTypes.string,
    showCategories: PropTypes.bool,
    showLovemeter: PropTypes.bool,
    showExplanation: PropTypes.bool,
    showFavoriteButton: PropTypes.bool,
    isSelected: PropTypes.bool,
};

export default ProductCard;
